import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCancelRegistration, useFlag, usePassFreezes, useQualificationMe, useRegister } from '../api/hooks';
import { accentOf, Medallion } from '../components/achievements/Medallion';
import { ErrorView, Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { QualificationChart } from '../components/qualification/QualificationChart';
import { DecayIcon, FloorIcon, TicketIcon, WarnIcon } from '../components/qualification/icons';
import { backButton, haptics } from '../telegram';
import { plural, shortDate, timeLabel } from '../format';
import { SnowIcon } from './PassScreen';
import type { QualPosition } from '../api/types';

/** Q2 «Моя позиция»: место+рейтинг, дистанция, график, статистика, история. */
export function MyQualificationScreen() {
  const navigate = useNavigate();
  const q = useQualificationMe();

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorView message={(q.error as Error)?.message} />;
  const data = q.data;
  if (data.status === 'none' || !data.player) {
    return (
      <div className="q-screen">
        <Head title="Моя позиция" onBack={() => navigate(-1)} />
        <div className="q-empty">
          Сыграй первый турнир отбора — все стартуют с {data.qualification?.startRating ?? 1500}.
        </div>
      </div>
    );
  }

  return <PositionBody data={data} own onBack={() => navigate(-1)} title="Моя позиция" />;
}

export function Head({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="subhead">
      <button className="iconbtn" onClick={onBack} aria-label="Назад">
        <BackIcon />
      </button>
      <div className="title">{title}</div>
    </div>
  );
}

/** Общее тело Q2/Б (own управляет призывами и разбивками). */
export function PositionBody({
  data,
  own,
  onBack,
  title,
}: {
  data: QualPosition;
  own: boolean;
  onBack: () => void;
  title: string;
}) {
  const p = data.player!;
  const cfg = data.qualification!;
  const [openRow, setOpenRow] = useState<string | null>(null);
  // 5.1: вход на экран заморозок — только для своей позиции
  const passEnabled = useFlag('pass');
  const freezes = usePassFreezes(passEnabled && own);
  const freezesOn =
    own && freezes.data?.status === 'active' && Boolean(freezes.data.freezesEnabled);
  const fb = freezes.data?.balances ?? { game1: 0, game3: 0, week: 0 };
  const inZone = p.rank !== null && p.rank <= cfg.tickets;
  const calibrating = p.state === 'calibrating';

  return (
    <div className="q-screen">
      <Head title={title} onBack={onBack} />

      {/* 5.1: вход на экран заморозок — из вкладки «Отбор» (макет) */}
      {freezesOn && (
        <Link className="bpf-entry" to="/pass/freezes">
          <SnowIcon size={14} />
          <span className="nm">Заморозки</span>
          <span className="cnt mono">
            {fb.game1 + fb.game3 + fb.week > 0 ? `×${fb.game1 + fb.game3 + fb.week}` : ''}
          </span>
          <span className="chev">›</span>
        </Link>
      )}

      <div className="q-pos-hero">
        <div className="q-pos-place">
          <div className="q-lbl">МЕСТО</div>
          <div className="v mono">
            {p.rank !== null ? p.rank : '—'}
            {p.rankChangeWeek !== null && p.rankChangeWeek !== 0 && (
              <span className={`q-shift ${p.rankChangeWeek > 0 ? 'up' : 'dn'}`}>
                {p.rankChangeWeek > 0 ? `▲${p.rankChangeWeek}` : `▼${-p.rankChangeWeek}`}
              </span>
            )}
          </div>
        </div>
        <div className="q-pos-rating">
          <div className="q-lbl">РЕЙТИНГ</div>
          <div className="v mono">{p.rating}</div>
          <div className="sub mono">{p.weekDelta >= 0 ? `+${p.weekDelta}` : p.weekDelta} за неделю</div>
        </div>
      </div>

      {/* Плашка дистанции */}
      {calibrating ? (
        <div className="q-dist q-dist--calib">
          <div className="q-dist-title">
            Ещё {Math.max(0, p.calGames - p.gamesPlayed)}{' '}
            {plural(Math.max(0, p.calGames - p.gamesPlayed), ['игра', 'игры', 'игр'])} до зачёта
          </div>
          <div className="q-calib-bigsegs">
            {Array.from({ length: p.calGames }, (_, i) => (
              <i key={i} className={i < p.gamesPlayed ? 'on' : ''} />
            ))}
          </div>
          {p.rankIfQualified !== null && <div className="q-dist-sub">Сейчас был бы #{p.rankIfQualified}</div>}
          <div className="q-dist-sub">Пока идёт калибровка, рейтинг меняется сильнее.</div>
        </div>
      ) : inZone ? (
        <div className="q-dist q-dist--in">
          <div className="q-dist-title">
            <TicketIcon size={14} /> Ты в зоне отбора
          </div>
          <div className="q-dist-sub">
            Отрыв от #{cfg.tickets + 1} — <b className="mono">{p.gapToTop3 !== null ? -p.gapToTop3 : '—'}</b>
          </div>
        </div>
      ) : (
        <div className="q-dist">
          <div className="q-dist-title">
            До #{cfg.tickets} — <span className="q-dist-num mono">{p.gapToTop3 ?? '—'}</span> очков
          </div>
          {p.gapToTop3 !== null && p.gapToTop3 <= 25 && (
            <div className="q-dist-sub">Примерно один хороший вечер</div>
          )}
          {data.scaleNeighbors && data.scaleNeighbors.length >= 2 && (
            <DistanceScale neighbors={data.scaleNeighbors} tickets={cfg.tickets} />
          )}
        </div>
      )}

      {/* Полосы состояний */}
      {own && p.state === 'warning' && (
        <div className="q-stateband q-stateband--warn">
          <WarnIcon size={14} /> {p.missedInRow} пропуска подряд: −{cfg.softStep} за каждый. С {cfg.hardFrom}-го — −{cfg.hardStep} за игру
        </div>
      )}
      {own && p.state === 'decaying' && (
        <div className="q-stateband q-stateband--decay">
          <DecayIcon size={14} /> Рейтинг снижается на {cfg.hardStep} за каждый пропуск · уже потеряно −{p.missedTotal}
        </div>
      )}
      {own && p.state === 'at_floor' && (
        <div className="q-stateband q-stateband--floor">
          <FloorIcon size={14} /> Ниже {cfg.floorRating} рейтинг не опускается
        </div>
      )}

      {data.chart && data.chart.length >= 2 && (
        <QualificationChart
          points={data.chart}
          startRating={cfg.startRating}
          cutoff={p.cutoffRating}
          tickets={cfg.tickets}
        />
      )}

      {own && data.nextTournament && data.status === 'active' && (
        <NextTournamentCard data={data} cfg={cfg} missWarning={p.missedInRow === 1} />
      )}

      <div className="q-stats">
        <div className="q-stat">
          <div className="v mono">
            {p.gamesPlayed}
            <span className="of">/{data.gamesAvailable ?? '—'}</span>
          </div>
          <div className="l">сыграно</div>
        </div>
        <div className="q-stat">
          <div className="v mono">{p.bestPlace !== null ? `#${p.bestPlace}` : '—'}</div>
          <div className="l">лучшее место</div>
        </div>
        <div className="q-stat">
          <div className="v mono">{p.playersBeatenTotal}</div>
          <div className="l">обыграно всего</div>
        </div>
        <div className="q-stat">
          <div className="v mono up">+{p.participationTotal}</div>
          <div className="l">за участие</div>
        </div>
        <div className="q-stat">
          <div className="v mono q-neg">−{p.missedTotal}</div>
          <div className="l">на пропусках</div>
        </div>
        <div className="q-stat">
          <div className="v mono">
            {cfg.startRating} → {p.rating}
          </div>
          <div className="l">с начала отбора</div>
        </div>
      </div>

      {own && data.nearestAchievement && (
        <Link
          className="q-next-ach"
          to={`/achievements/${data.nearestAchievement.achievementId}`}
        >
          <Medallion
            icon={data.nearestAchievement.icon}
            level={data.nearestAchievement.level}
            maxLevel={data.nearestAchievement.maxLevel}
            progress={
              data.nearestAchievement.nextTarget > 0
                ? Math.min(1, data.nearestAchievement.progress / data.nearestAchievement.nextTarget)
                : null
            }
            size={46}
            variant={accentOf(
              data.nearestAchievement.code,
              data.nearestAchievement.level,
              data.nearestAchievement.maxLevel,
            )}
          />
          <div className="q-next-ach-main">
            <div className="q-lbl">БЛИЖАЙШЕЕ ДОСТИЖЕНИЕ</div>
            <div className="nm">{data.nearestAchievement.name}</div>
            <div className="sub">{data.nearestAchievement.hint}</div>
          </div>
          <span className="q-next-ach-chev">›</span>
        </Link>
      )}

      {data.history && data.history.length > 0 && (
        <>
          <div className="q-overline">ИСТОРИЯ ТУРНИРОВ</div>
          <div className="q-history">
            {data.history.map((h) => {
              const key = `${h.gameId}-${h.kind}`;
              const open = openRow === key;
              return (
                <div key={key} className="q-hist-row-wrap">
                  <button
                    className="q-hist-row"
                    onClick={() => {
                      if (!own || h.kind === 'miss') return;
                      haptics.tap();
                      setOpenRow(open ? null : key);
                    }}
                  >
                    <span className="dt mono">{shortDate(h.date)}</span>
                    {h.kind === 'miss' ? (
                      <span className={h.frozen ? 'what q-frozen' : 'what q-neg'}>
                        {h.frozen ? (
                          <>
                            Пропуск · <SnowIcon size={10} /> заморожен
                          </>
                        ) : (
                          'Пропуск'
                        )}
                      </span>
                    ) : (
                      <span className="what">
                        {h.place !== null ? `${h.place} место` : 'сыграно'}
                        {h.fieldSize !== null && ` из ${h.fieldSize}`}
                      </span>
                    )}
                    <span className={`d mono ${h.kind === 'miss' ? 'q-neg' : h.delta > 0 ? 'up' : ''}`}>
                      {h.delta >= 0 ? `+${h.delta}` : h.delta}
                    </span>
                  </button>
                  {open && h.breakdown && (
                    <div className="q-hist-breakdown">
                      <div className="row">
                        <span>За результат</span>
                        <span className={`mono ${h.breakdown.resultShown < 0 ? '' : 'up'}`}>
                          {h.breakdown.resultShown >= 0 ? `+${h.breakdown.resultShown}` : h.breakdown.resultShown}
                        </span>
                      </div>
                      <div className="row">
                        <span>За участие</span>
                        <span className="mono up">+{h.breakdown.bonus}</span>
                      </div>
                      {h.breakdown.floorShown > 0 && (
                        <div className="row">
                          <span>Защита старта</span>
                          <span className="mono">+{h.breakdown.floorShown}</span>
                        </div>
                      )}
                      <div className="row total">
                        <span>Итого</span>
                        <span className="mono">
                          {h.breakdown.total >= 0 ? `+${h.breakdown.total}` : h.breakdown.total}
                        </span>
                      </div>
                      {h.breakdown.playersBeaten !== null && h.fieldSize !== null && (
                        <div className="note">
                          Обошёл {h.breakdown.playersBeaten} из {h.fieldSize - 1} соперников
                        </div>
                      )}
                      {h.breakdown.beatRaceLeader && <div className="note q-gap">Обошёл лидера гонки</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** Шкала дистанции: трек от меня до #tickets, точки соседей, коллизии <20px. */
function DistanceScale({
  neighbors,
  tickets,
}: {
  neighbors: NonNullable<QualPosition['scaleNeighbors']>;
  tickets: number;
}) {
  const me = neighbors.find((n) => n.isMe);
  if (!me) return null;
  const ratings = neighbors.map((n) => n.rating);
  const min = Math.min(...ratings);
  const max = Math.max(...ratings);
  if (max === min) return null;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const mePct = pct(me.rating);
  // Коллизии подписей: скрыть дальнюю от меня из пары ближе 6% (~20px/320)
  const shown = neighbors.filter((n) => {
    if (n.isMe) return true;
    const near = neighbors.some(
      (o) =>
        !o.isMe &&
        o.playerId !== n.playerId &&
        Math.abs(pct(o.rating) - pct(n.rating)) < 6 &&
        Math.abs(pct(o.rating) - mePct) < Math.abs(pct(n.rating) - mePct),
    );
    return !near;
  });
  const edge = neighbors.find((n) => n.rank === tickets);
  return (
    <div className="q-scale">
      <div className="q-scale-track">
        {edge && (
          <i
            className="q-scale-fill"
            style={{
              left: `${Math.min(mePct, pct(edge.rating))}%`,
              width: `${Math.abs(pct(edge.rating) - mePct)}%`,
            }}
          />
        )}
        {shown.map((n) => (
          <span
            key={n.playerId}
            className={`q-scale-dot ${n.isMe ? 'me' : ''}`}
            style={{ left: `${pct(n.rating)}%` }}
          />
        ))}
      </div>
      <div className="q-scale-labels">
        {shown.map((n) => (
          <span
            key={n.playerId}
            className={`q-scale-label mono ${n.isMe ? 'me' : ''}`}
            style={{ left: `${pct(n.rating)}%` }}
          >
            {n.isMe ? 'ТЫ' : `#${n.rank}`}
          </span>
        ))}
      </div>
    </div>
  );
}

function NextTournamentCard({
  data,
  cfg,
  missWarning,
}: {
  data: QualPosition;
  cfg: NonNullable<QualPosition['qualification']>;
  missWarning: boolean;
}) {
  const t = data.nextTournament!;
  const register = useRegister(t.id);
  const cancel = useCancelRegistration(t.id);
  return (
    <div className="q-nextcard">
      <div className="q-nextcard-top">
        <div>
          <div className="when">
            {shortDate(t.startTime)} · {timeLabel(t.startTime)}
          </div>
          <div className="cnt">{t.registeredCount} записан{t.registeredCount % 10 === 1 && t.registeredCount % 100 !== 11 ? '' : 'о'}</div>
        </div>
        <div className="bonus up mono">+{cfg.bonus} к баллам отбора</div>
      </div>
      {missWarning && (
        <div className="q-nextcard-warn">
          Пропустишь ещё один — начнётся −{cfg.softStep} за игру
        </div>
      )}
      {t.registeredByMe ? (
        <div className="q-nextcard-actions">
          <button className="q-next-btn q-next-btn--in" disabled>
            ✓ Ты записан
          </button>
          <button
            className="q-nextcard-cancel"
            onClick={() => {
              haptics.tap();
              void cancel.mutateAsync().catch(() => undefined);
            }}
          >
            Отменить запись
          </button>
        </div>
      ) : (
        <button
          className="btn"
          style={{ width: '100%' }}
          disabled={register.isPending}
          onClick={() => {
            track_register(t.id);
            void register
              .mutateAsync()
              .then(() => haptics.success())
              .catch(() => haptics.error());
          }}
        >
          {register.isPending ? 'Запись…' : 'Записаться'}
        </button>
      )}
    </div>
  );
}

function track_register(id: number) {
  import('../analytics').then(({ track }) => track('qualification_register', { tournamentId: id, from: 'position' }));
}
